import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import DOMPurify from 'dompurify';
import { PAGES, PAGE_IDS } from 'js/constants/Pages';
import { Link } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { submitGalleryEntry } from 'js/store/gallery/Gallery.actions';
import { getGallerySubmissionFetching } from 'js/store/gallery/Gallery.selectors';

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const ArtworkSubmissionForm = () => {
  const dispatch = useDispatch();
  const isFetching = useSelector((state) => {
    return getGallerySubmissionFetching(state);
  });
  const [formData, setFormData] = useState({
    childName: '',
    childAge: '',
    name: '',
    email: '',
    consentArtwork: false,
    consentTerms: false,
    displayName: false,
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadError, setUploadError] = useState('');

  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      // case 'childName':
      //   if (!value.trim()) {
      //     error = "Child's name is required";
      //   } else if (value.trim().length < 2) {
      //     error = 'Name must be at least 2 characters';
      //   }
      //   break;

      // case 'childAge':
      //   if (!value.trim()) {
      //     error = "Child's age is required";
      //   } else if (!/^\d+$/.test(value.trim())) {
      //     error = 'Age must be a number';
      //   } else if (parseInt(value) < 0 || parseInt(value) > 18) {
      //     error = 'Age must be between 0 and 18';
      //   }
      //   break;

      case 'name':
        if (!value.trim()) {
          error = 'Full name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email address is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value.trim())) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'consentArtwork':
        if (!value) {
          error = 'You must confirm consent to submit artwork';
        }
        break;

      case 'consentTerms':
        if (!value) {
          error = 'You must agree to the Terms & Conditions and Privacy Policy';
        }
        break;

      default:
        break;
    }

    return error;
  };

  const sanitizeInput = (value) => {
    if (typeof value === 'string') {
      return DOMPurify.sanitize(value.trim(), { ALLOWED_TAGS: [] });
    }
    return value;
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    setUploadError('');

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
        setUploadError('Please upload only JPG, JPEG, or PNG image files');
      } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
        setUploadError('File size must be less than 10MB');
      } else {
        setUploadError('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      // Additional type check
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setUploadError('Please upload only JPG, JPEG, or PNG image files');
        return;
      }

      setUploadedFile(file);

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Cleanup preview URL on unmount or when file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear error when user starts typing/changing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    const error = validateField(name, fieldValue);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    // Check if file is uploaded
    if (!uploadedFile) {
      setUploadError('Please upload an artwork image');
      newErrors.upload = true;
    }

    // If there are errors, set them and stop submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorElement = document.querySelector('.form-field--error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    // Sanitize form data before submission
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      text: '',
      email: sanitizeInput(formData.email),
      private_data: {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
      },
      public_data: {
        childName: sanitizeInput(formData.childName),
        childAge: sanitizeInput(formData.childAge),
        consentArtwork: formData.consentArtwork,
        consentTerms: formData.consentTerms,
        displayName: formData.displayName,
      },
    };

    const file = await getBase64(uploadedFile);
    sanitizedData.file = file;

    dispatch(
      submitGalleryEntry(sanitizedData, () => {
        // Reset form after successful submission
        setFormData({
          childName: '',
          childAge: '',
          name: '',
          email: '',
          consentArtwork: false,
          consentTerms: false,
          displayName: false,
        });
        setUploadedFile(null);
        setPreviewUrl(null);
        setErrors({});
        setUploadError('');
      })
    );
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setUploadedFile(null);
    setPreviewUrl(null);
    setUploadError('');
  };

  return (
    <form onSubmit={handleSubmit} id="artwork-submission-form">
      {isFetching && (
        <div className="submitting-message">
          <div className="coloured-background" />
          <p>Submitting your artwork...</p>
        </div>
      )}

      <div
        {...getRootProps()}
        className={`upload-zone ${isDragActive ? 'upload-zone--active' : ''} ${
          previewUrl ? 'upload-zone--has-image' : ''
        } ${uploadError ? 'upload-zone--error' : ''}`}
      >
        <input {...getInputProps()} />
        {!previewUrl ? (
          <div className="upload-zone__content">
            <h2 className="upload-zone__title">Drag here or tap to upload</h2>
            <div className="upload-zone__icon">
              <svg viewBox="0 0 24 24" className="upload-zone__plus">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </div>
            <p className="upload-zone__hint">JPG, JPEG or PNG (max 10MB)</p>
          </div>
        ) : (
          <div className="upload-zone__preview">
            <img src={previewUrl} alt="Uploaded artwork" className="upload-zone__image" />
            <button type="button" onClick={handleRemoveImage} className="upload-zone__remove" aria-label="Remove image">
              ×
            </button>
            <p className="upload-zone__filename">{uploadedFile.name}</p>
          </div>
        )}
      </div>
      {uploadError && <p className="error-message">{uploadError}</p>}

      <section className="form-section">
        <h2 className="form-section__title">Child's details</h2>
        <div className="form-section__row">
          <div className={`form-field ${errors.childName ? 'form-field--error' : ''}`}>
            <label className="form-field__label">Child's Name</label>
            <input
              type="text"
              name="childName"
              value={formData.childName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter first name"
              className="form-field__input"
              aria-invalid={!!errors.childName}
              aria-describedby={errors.childName ? 'childName-error' : undefined}
            />
            {errors.childName && (
              <span className="error-message" id="childName-error">
                {errors.childName}
              </span>
            )}
          </div>

          <div className={`form-field ${errors.childAge ? 'form-field--error' : ''}`}>
            <label className="form-field__label">Child's Age</label>
            <input
              type="number"
              name="childAge"
              value={formData.childAge}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter child's age"
              className="form-field__input"
              aria-invalid={!!errors.childAge}
              aria-describedby={errors.childAge ? 'childAge-error' : undefined}
            />
            {errors.childAge && (
              <span className="error-message" id="childAge-error">
                {errors.childAge}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="form-section">
        <h2 className="form-section__title">Parent/Guardian details</h2>
        <div className="form-section__row">
          <div className={`form-field ${errors.name ? 'form-field--error' : ''}`}>
            <label className="form-field__label">
              Full name <span className="form-field__required">(required)</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter first and last name"
              className="form-field__input"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <span className="error-message" id="name-error">
                {errors.name}
              </span>
            )}
          </div>

          <div className={`form-field ${errors.email ? 'form-field--error' : ''}`}>
            <label className="form-field__label">
              Email address <span className="form-field__required">(required)</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter email address"
              className="form-field__input"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <span className="error-message" id="email-error">
                {errors.email}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="form-section">
        <h4 className="form-section__subtitle">
          Consent and legal <span className="form-field__required">(required)</span>
        </h4>

        <div className={`checkbox-wrapper ${errors.consentArtwork ? 'checkbox-wrapper--error' : ''}`}>
          <label className="checkbox">
            <input
              type="checkbox"
              name="consentArtwork"
              checked={formData.consentArtwork}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className="checkbox__input"
              aria-invalid={!!errors.consentArtwork}
              aria-describedby={errors.consentArtwork ? 'consentArtwork-error' : undefined}
            />
            <span className="checkbox__label">
              I confirm I am the parent/legal guardian and have the right to submit this artwork, and grant permission
              for it to appear on the website, social media, and Channel 5 Milkshake!
            </span>
          </label>
          {errors.consentArtwork && (
            <span className="error-message" id="consentArtwork-error">
              {errors.consentArtwork}
            </span>
          )}
        </div>

        <div className={`checkbox-wrapper ${errors.consentTerms ? 'checkbox-wrapper--error' : ''}`}>
          <label className="checkbox">
            <input
              type="checkbox"
              name="consentTerms"
              checked={formData.consentTerms}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className="checkbox__input"
              aria-invalid={!!errors.consentTerms}
              aria-describedby={errors.consentTerms ? 'consentTerms-error' : undefined}
            />
            <span className="checkbox__label">
              I agree to the{' '}
              <Link className="checkbox__link" to={`${PAGES[PAGE_IDS.terms].base_url}`}>
                {PAGES[PAGE_IDS.terms].name}
              </Link>{' '}
              and{' '}
              <Link className="checkbox__link" to={`${PAGES[PAGE_IDS.privacy_policy].base_url}`}>
                {PAGES[PAGE_IDS.privacy_policy].name}
              </Link>
              .
            </span>
          </label>
          {errors.consentTerms && (
            <span className="error-message" id="consentTerms-error">
              {errors.consentTerms}
            </span>
          )}
        </div>
      </section>

      <section className="form-section">
        <h4 className="form-section__subtitle">Display of child's first name and age</h4>

        <label className="checkbox">
          <input
            type="checkbox"
            name="displayName"
            checked={formData.displayName}
            onChange={handleInputChange}
            className="checkbox__input"
          />
          <span className="checkbox__label">
            I consent to my child's first name and age being displayed alongside the artwork
          </span>
        </label>
      </section>

      <button type="submit" className="submit-button">
        Submit artwork
      </button>
    </form>
  );
};

export default ArtworkSubmissionForm;
